import { SettingsFieldRow, SettingsSection } from 'components/Settings';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import useDeleteCourse from 'hooks/course/useDeleteCourse';
import useCourseDetailsForm from 'hooks/settings/useCourseDetailsForm';
import { LANGUAGE_OPTIONS } from 'locales/constants';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, Form, Stack, Text, TextInput, Tooltip } from 'shell/components';
import { COURSE_DETAILS_FIELD } from 'utils/settings';
import { SettingsPageContext } from '../../PageProvider';

const CourseDetails = ({ sectionId }) => {
    const { course } = useApp();
    const { auth } = useAuth();
    const { setSectionDirty } = useContext(SettingsPageContext);
    const { t } = useTranslation();
    const { form, errors, updateAndSave, updateField, save, saveField } = useCourseDetailsForm({
        onDirtyChange: (value) => setSectionDirty('course-details', value),
    });
    const { openDeleteCourseDialog } = useDeleteCourse({ course, courseId: auth.course_id });

    return (
        <SettingsSection id={sectionId} title={t('course-settings.course-details.title', 'Course Details')}>
            <Stack spacing={4}>
                <Form
                    onSubmit={(event) => {
                        event.preventDefault();
                        save();
                    }}
                >
                    <SettingsFieldRow label={<Text variant='p2'>{t('course-settings.title', 'Course Settings')}</Text>}>
                        <Stack spacing={4} sx={{ width: '100%' }}>
                            <TextInput
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                                label={t('course-settings.course-details.course-name-label', 'Course Name')}
                                required
                                value={form.name}
                                onChange={(event) => updateField(COURSE_DETAILS_FIELD.NAME, event.target.value)}
                                onBlur={() => saveField(COURSE_DETAILS_FIELD.NAME)}
                            />
                            <Autocomplete
                                disableClearable
                                label={
                                    <Stack alignItems='center' direction='row' spacing={2}>
                                        <Text>
                                            {t(
                                                'course-settings.course-details.feedback-language-label',
                                                'Feedback Language'
                                            )}
                                        </Text>
                                        <Tooltip
                                            content={t(
                                                'course-settings.course-details.feedback-language-tooltip',
                                                'Choose the language you want assignment feedback to be delivered in. If you change this setting midway through a course, any new feedback generated from that point on will use the updated language.'
                                            )}
                                            type='info'
                                        />
                                    </Stack>
                                }
                                options={LANGUAGE_OPTIONS}
                                isOptionEqualToValue={(option, value) => option.key === value?.key}
                                placeholder={t('ui.select', 'Select')}
                                value={form.language}
                                onChange={(_, value) => updateAndSave(COURSE_DETAILS_FIELD.LANGUAGE, value)}
                            />
                            <Button
                                type='button'
                                color='destructive'
                                variant='secondary'
                                onClick={openDeleteCourseDialog}
                            >
                                {t('course:course-lobby.actions.delete', 'Delete Course')}
                            </Button>
                        </Stack>
                    </SettingsFieldRow>
                </Form>
            </Stack>
            {/* TODO: uncomment after assignment auto import is implemented */}

            {/* <Stack spacing={2}>
                <Stack alignItems='center' direction='row' justifyContent='space-between'>
                    <Text variant='p2' weight='medium'>
                        {t('settings-updates.import-existing-lms-assignments-title', 'Import Existing LMS Assignments')}
                    </Text>
                    <Switch
                        aria-label={t(
                            'settings-updates.import-existing-lms-assignments-title',
                            'Import Existing LMS Assignments'
                        )}
                        checked={false}
                        onChange={handleImportExistingAssignmentsChange}
                    />
                </Stack>
                <Text color='light' variant='p2'>
                    {t(
                        'settings-updates.import-existing-lms-assignments-description',
                        'Import existing assignments into the LMS course only when entering the course for the first time, minimizing the manual setup required.'
                    )}
                </Text>
            </Stack> */}
        </SettingsSection>
    );
};

export default CourseDetails;
